
import { defineComponent, ref, onMounted, computed, toRefs } from "vue";
import { MemberApi } from "@/services/Api";
import { useI18n } from "vue-i18n";
import { ConsumerTokenRequest, KlarnaSessionRequest, OrderLine, BillingAddress, KlarnaCustomer } from "@/models/FlowPortal";

declare global {
  interface Window {
    klarnaAsyncCallback?: () => void;
  }
}
declare const Klarna: any;

export default defineComponent({
  props: {
    activationcode: {
      type: String,
      required: true,
    },
    person: {
      type: Object,
      required: true,
    }

  },
  setup(props) {
    const activationCode = ref<string>(props.activationcode);
    const isLoading = ref<boolean>(false);
    const activationSuccessful = ref<boolean>(false);
    const activationFailed = ref<boolean>(false)
    const person: any = ref(props.person);
    const orderLines: OrderLine[] = person.value.OrderLines;

    const subscriptionModel = ref({
      activationCode: activationCode,
      name: props.person.Name,
      ssn: '',
      lastName: props.person.Lastname,
      email: props.person.Email,
      line1: props.person.Line1,
      zip: props.person.Zip,
      city: props.person.City,
      country: props.person.Country,
      mobilePhone: props.person.MobilePhone
    });

    const { t } = useI18n();
    const errorMessage = ref<string>();
    const klarnaSession = ref();

    const loadKlarnaSDK = () => {
      const script = document.createElement('script');
      script.src = 'https://x.klarnacdn.net/kp/lib/v1/api.js';
      script.async = true;
      document.body.appendChild(script);
    };

    const initializeAndLoadKlarna = () => {
      window.klarnaAsyncCallback = function () {
        try {
          Klarna.Payments.init({
            client_token: klarnaSession.value.client_token
          })
          Klarna.Payments.load(
            {
              container: '#klarna-payments-container'
            },
            {},
            function (res) {
              // console.debug(res);
            }
          )
        } catch (e) {
          console.log("Error: ", e);
        }
      };
    }

    const authorizeInKlarna = async () => {
      Klarna.Payments.authorize(
        {},
        {
          billing_address: {
            given_name: subscriptionModel.value.name,
            family_name: subscriptionModel.value.lastName,
            email: subscriptionModel.value.email,
            street_address: subscriptionModel.value.line1,
            postal_code: subscriptionModel.value.zip,
            city: subscriptionModel.value.city,
            phone: subscriptionModel.value.mobilePhone,
            country: person.value.PurchaseCountry
          },
          customer: {
            national_identification_number: subscriptionModel.value.ssn
          }
        },
        async function (res) {
          if (res.approved) {
            isLoading.value = true;
            document.body.scrollTop = 0;
            document.documentElement.scrollTop = 0;

            const billingAddress: BillingAddress = {
              city: subscriptionModel.value.city,
              country: person.value.PurchaseCountry,
              email: subscriptionModel.value.email,
              familyName: subscriptionModel.value.lastName,
              givenName: subscriptionModel.value.name,
              phone: subscriptionModel.value.mobilePhone,
              postalCode: subscriptionModel.value.zip,
              streetAddress: subscriptionModel.value.line1
            }

            const klarnaCustomer: KlarnaCustomer = {
              nationalIdentificationNumber: subscriptionModel.value.ssn
            }

            const tokenRequest: ConsumerTokenRequest = {
              authorizationToken: res.authorization_token,
              activationCode: subscriptionModel.value.activationCode,
              description: "This token will be used to charge customers for their pellets subscription",
              locale: "sv-SE",
              purchaseCountry: person.value.PurchaseCountry,
              purchaseCurrency: person.value.PurchaseCurrency,
              billingAddress: billingAddress,
              klarnaCustomer: klarnaCustomer
            }

            var response = await MemberApi.generateConsumerToken(tokenRequest);
            isLoading.value = false;

            if (response == true) {
              activationSuccessful.value = true;
            } else {
              activationFailed.value = true;
            }
          }
        }
      );
    }

    const isFormValid = computed(() => {
      const form = document.getElementById('subscriptionForm') as HTMLFormElement | null;
      const formCheck = form?.checkValidity() ? form?.checkValidity() : true;
      return formCheck && subscriptionModel.value.name &&
        subscriptionModel.value.lastName &&
        subscriptionModel.value.email &&
        subscriptionModel.value.line1 &&
        subscriptionModel.value.zip &&
        subscriptionModel.value.city &&
        subscriptionModel.value.country &&
        (subscriptionModel.value.mobilePhone && subscriptionModel.value.mobilePhone.length === 10);
    })

    onMounted(async () => {

      const klarnaSessionRequest: KlarnaSessionRequest = {
        locale: "sv-SE",
        orderAmount: person.value.OrderAmount,
        purchaseCountry: person.value.PurchaseCountry,
        purchaseCurrency: person.value.PurchaseCurrency,
        intent: "tokenize",
        orderLines: orderLines
      }
      klarnaSession.value = await MemberApi.createKlarnaSession(klarnaSessionRequest);
      loadKlarnaSDK();
      initializeAndLoadKlarna();
    });

    return {
      subscriptionModel,
      errorMessage,
      isFormValid,
      authorizeInKlarna,
      isLoading,
      activationSuccessful,
      activationFailed
    };
  },
  methods: {
    validatePhone(value) {
      if (!value || value.length !== 10) {
        return false;
      }

      const regex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/i;
      return regex.test(value);
    },
    validateSSN(value: string) {
      const ssnRegex = /^(19|20)?(\d{2})(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])[-+]?(\d{4})$/;
      return ssnRegex.test(value);
    }
  }
});
